import type { DocumentType } from '../types';

/**
 * Required Document Configuration for Collections/Hardship Claims
 */
export interface RequiredDocumentConfig {
  type: DocumentType;
  name: string;
  description: string;
  purpose: string;
  required: boolean;
  conditionalOn?: string; // Only required if this condition is met
  acceptedFormats: string[];
  maxSizeMB: number;
  expirationMonths?: number;
  examples: string[];
}

/**
 * Collections Required Documents Configuration
 * Documents needed to verify financial hardship and process payment arrangements
 */
export const REQUIRED_DOCUMENTS: RequiredDocumentConfig[] = [
  {
    type: 'identity_document',
    name: 'Identity Document',
    description: 'Government-issued photo identification document.',
    purpose: 'Verify your identity to protect your account and comply with regulations.',
    required: true,
    acceptedFormats: ['PDF', 'PNG', 'JPG'],
    maxSizeMB: 10,
    examples: [
      'Passport',
      'National ID Card (DNI)',
      'Driver\'s License',
      'Residence Permit',
    ],
  },
  {
    type: 'proof_of_address',
    name: 'Proof of Address',
    description: 'Recent document showing your current residential address.',
    purpose: 'Confirm your current address for correspondence and legal requirements.',
    required: true,
    acceptedFormats: ['PDF', 'PNG', 'JPG'],
    maxSizeMB: 10,
    expirationMonths: 3,
    examples: [
      'Utility Bill (electricity, gas, water)',
      'Bank Statement',
      'Official Government Letter',
      'Rental Agreement',
    ],
  },
  {
    type: 'income_statement',
    name: 'Proof of Income',
    description: 'Documentation showing your current income or financial situation.',
    purpose: 'Assess your ability to pay and determine appropriate payment plan options.',
    required: true,
    acceptedFormats: ['PDF', 'PNG', 'JPG', 'DOCX'],
    maxSizeMB: 10,
    expirationMonths: 3,
    examples: [
      'Recent Payslips (last 3 months)',
      'Employment Contract',
      'Pension Statement',
      'Self-Employment Tax Return',
      'Social Security Benefits Statement',
    ],
  },
  {
    type: 'bank_statement',
    name: 'Bank Statement',
    description: 'Recent bank statements showing your financial transactions.',
    purpose: 'Understand your financial situation to offer suitable payment arrangements.',
    required: true,
    acceptedFormats: ['PDF', 'PNG', 'JPG'],
    maxSizeMB: 10,
    expirationMonths: 3,
    examples: [
      'Bank Account Statement (last 3 months)',
      'Online Banking PDF Export',
    ],
  },
  {
    type: 'hardship_declaration',
    name: 'Hardship Declaration',
    description: 'Written statement explaining your financial difficulties.',
    purpose: 'Document the circumstances affecting your ability to pay for hardship review.',
    required: false,
    conditionalOn: 'hardship_claim',
    acceptedFormats: ['PDF', 'DOCX', 'PNG', 'JPG'],
    maxSizeMB: 5,
    examples: [
      'Signed Hardship Letter',
      'Financial Difficulty Statement',
      'Hardship Application Form',
    ],
  },
  {
    type: 'medical_certificate',
    name: 'Medical Certificate',
    description: 'Medical documentation if health issues affect your ability to pay.',
    purpose: 'Support hardship claims related to medical conditions or disability.',
    required: false,
    conditionalOn: 'medical_hardship',
    acceptedFormats: ['PDF', 'PNG', 'JPG'],
    maxSizeMB: 10,
    expirationMonths: 6,
    examples: [
      'Doctor\'s Certificate',
      'Hospital Discharge Summary',
      'Disability Assessment',
      'Medical Report',
    ],
  },
  {
    type: 'unemployment_certificate',
    name: 'Unemployment Certificate',
    description: 'Official proof of unemployment or job loss.',
    purpose: 'Verify unemployment status for hardship consideration.',
    required: false,
    conditionalOn: 'unemployment',
    acceptedFormats: ['PDF', 'PNG', 'JPG'],
    maxSizeMB: 5,
    examples: [
      'Unemployment Benefits Letter',
      'Employment Termination Letter',
      'Redundancy Notice',
      'Job Seeker Registration',
    ],
  },
  {
    type: 'payment_proof',
    name: 'Payment Proof',
    description: 'Evidence of payments made towards your debt.',
    purpose: 'Track and verify payments for accurate account balance.',
    required: false,
    acceptedFormats: ['PDF', 'PNG', 'JPG'],
    maxSizeMB: 5,
    examples: [
      'Bank Transfer Receipt',
      'Payment Confirmation',
      'Direct Debit Statement',
    ],
  },
];

/**
 * Get all required document types
 */
export function getRequiredDocumentTypes(): DocumentType[] {
  return REQUIRED_DOCUMENTS.filter(d => d.required).map(d => d.type);
}

/**
 * Get document configuration by type
 */
export function getDocumentConfig(type: DocumentType): RequiredDocumentConfig | undefined {
  return REQUIRED_DOCUMENTS.find(d => d.type === type);
}

/**
 * Get mandatory documents (always required)
 */
export function getMandatoryDocuments(): RequiredDocumentConfig[] {
  return REQUIRED_DOCUMENTS.filter(d => d.required && !d.conditionalOn);
}

/**
 * Get conditional documents based on claim type
 */
export function getConditionalDocuments(conditions: string[]): RequiredDocumentConfig[] {
  return REQUIRED_DOCUMENTS.filter(
    d => d.conditionalOn && conditions.includes(d.conditionalOn)
  );
}

/**
 * Document type labels for display
 */
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  identity_document: 'Identity Document',
  proof_of_address: 'Proof of Address',
  income_statement: 'Proof of Income',
  bank_statement: 'Bank Statement',
  employment_letter: 'Employment Letter',
  hardship_declaration: 'Hardship Declaration',
  medical_certificate: 'Medical Certificate',
  unemployment_certificate: 'Unemployment Certificate',
  payment_proof: 'Payment Proof',
  other: 'Other Document',
};
